/**
 * Main App Component
 *
 * Sets up routing and providers with language support.
 */

import { useEffect } from 'react';
import { Routes, Route, useNavigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider } from './lib/auth-context';
import { LanguageProvider } from './lib/language-context';
import { Layout } from './components/Layout';
import { ProtectedRoute } from './components/ProtectedRoute';
import { LandingPage } from './pages/LandingPage';
import BookCallPage from './pages/BookCallPage';
import TermsPage from './pages/TermsPage';
import { Dashboard } from './pages/Dashboard';
import { Assessments, AssessmentDetail } from './pages/Assessments';
import { Artifacts, ArtifactDetail } from './pages/Artifacts';
import { Rubrics, RubricDetail } from './pages/Rubrics';
import { Scores } from './pages/Scores';
import { Subscriptions } from './pages/Subscriptions';
import { Deliveries } from './pages/Deliveries';
import { Exports } from './pages/Exports';

// Trainer pages
import {
  TrainerDashboard,
  TrainerRubricList,
  CreateRubric,
  EditRubric,
  TrainerRubricDetail,
  TrainerAssessmentList,
  CreateAssessmentWizard,
  EditAssessment,
  TrainerAssessmentDetail,
  TrainerSubmissionResults,
} from './pages/trainer';

// Learner pages (minimal chrome, no Layout)
import { LearnerTest } from './pages/learner';

// Public pages
import { ResetPasswordPage } from './pages/ResetPasswordPage';

/**
 * Wrapper for Arabic public routes - sets up LanguageProvider and renders Outlet
 */
function ArabicLayout() {
  return (
    <LanguageProvider lang="ar">
      <Outlet />
    </LanguageProvider>
  );
}

/**
 * Wrapper for English public routes with localStorage preference redirect
 */
function EnglishLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // On root path, check for saved Arabic preference and redirect
    if (location.pathname === '/') {
      const savedLang = localStorage.getItem('preferredLanguage');
      if (savedLang === 'ar') {
        navigate('/ar', { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  return (
    <LanguageProvider lang="en">
      <Outlet />
    </LanguageProvider>
  );
}

export default function App() {
  return (
    <AuthProvider>
        <Routes>
          {/* Learner routes - token-based auth, no Layout wrapper, no translation */}
          <Route path="/learner/test/:assessmentId" element={<LearnerTest />} />

          {/* Password reset route (public - Supabase handles token from URL) */}
          <Route path="/reset-password" element={<ResetPasswordPage />} />

          {/* Protected operator routes under /console - no translation */}
          <Route
            path="/console/*"
            element={
              <ProtectedRoute>
                <Layout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="assessments" element={<Assessments />} />
            <Route path="assessments/:id" element={<AssessmentDetail />} />
            <Route path="artifacts" element={<Artifacts />} />
            <Route path="artifacts/:id" element={<ArtifactDetail />} />
            <Route path="rubrics" element={<Rubrics />} />
            <Route path="rubrics/:id" element={<RubricDetail />} />
            <Route path="scores" element={<Scores />} />
            <Route path="subscriptions" element={<Subscriptions />} />
            <Route path="deliveries" element={<Deliveries />} />
            <Route path="exports" element={<Exports />} />

            {/* Trainer routes */}
            <Route path="trainer" element={<TrainerDashboard />} />
            <Route path="trainer/rubrics" element={<TrainerRubricList />} />
            <Route path="trainer/rubrics/new" element={<CreateRubric />} />
            <Route path="trainer/rubrics/:id" element={<TrainerRubricDetail />} />
            <Route path="trainer/rubrics/:id/edit" element={<EditRubric />} />
            <Route path="trainer/assessments" element={<TrainerAssessmentList />} />
            <Route path="trainer/assessments/new" element={<CreateAssessmentWizard />} />
            <Route path="trainer/assessments/:id" element={<TrainerAssessmentDetail />} />
            <Route path="trainer/assessments/:id/edit" element={<EditAssessment />} />
            <Route path="trainer/submissions/:submissionId" element={<TrainerSubmissionResults />} />
          </Route>

          {/* Arabic public routes */}
          <Route path="/ar" element={<ArabicLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="book-call" element={<BookCallPage />} />
            <Route path="terms" element={<TermsPage />} />
          </Route>

          {/* English public routes (default) - with localStorage preference redirect */}
          <Route path="/" element={<EnglishLayout />}>
            <Route index element={<LandingPage />} />
            <Route path="book-call" element={<BookCallPage />} />
            <Route path="terms" element={<TermsPage />} />
          </Route>
        </Routes>
    </AuthProvider>
  );
}
